import {
  DateInput,
  FormGroup,
  FormGroupFeedback,
  Icon16,
  IconNames16,
  IFormGroupProps,
  Intent,
  IPopoverProps,
  IDateInputProps as IThumbtackDateInputProps,
} from '@pinpointhq/thumbtack';
import { format, isAfter, isBefore, parse } from 'date-fns';
import { FieldHelperProps, FieldInputProps, FieldMetaProps, useField } from 'formik';
import * as React from 'react';

interface IInputProps extends Omit<IThumbtackDateInputProps['inputProps'], 'intent' | 'leftIcon'> {
  /**
   * Name of a Thumbtack icon to render before the input.
   * The icon will be rendered as an `<svg>`.
   */
  leftIcon?: IconNames16;
}

interface IDateInputProps extends Omit<IThumbtackDateInputProps, 'inputProps'> {
  inputProps: IInputProps;
}

interface IDateField {
  name: string;
  formGroupProps?: Omit<IFormGroupProps, 'labelFor'>;
  dateInputProps?: IDateInputProps;
  popoverProps?: Pick<IPopoverProps, 'fill'>;
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
}

export default function DateField<T extends string>({
  dateInputProps,
  name,
  maxDate,
  minDate,
  disabled,
  formGroupProps,
  popoverProps,
}: IDateField) {
  const [field, meta, { setValue }]: [FieldInputProps<T>, FieldMetaProps<T>, FieldHelperProps<T>] = useField<T>(name);
  const hasError = meta.touched && meta.error;

  // If the date input has a max and min date and you start typing date fns does its best to create a date out of
  // any old shit (e.g. if you type in 2 it will set the date to 1st Feb 2021). The problem is that date could be
  // outside of the date range specified so we need to set the value to be null if that's the case.
  //
  const isDateInRange = (date: Date) => {
    if (maxDate && isAfter(date, maxDate)) return false;
    if (minDate && isBefore(date, minDate)) return false;

    return true;
  };

  let value: Date;
  if (field.value) {
    value = parse(field.value);

    value = isDateInRange(value) ? value : null;
  }

  function handleChange(selectedDate: Date, isUserChange: boolean) {
    if (isUserChange) {
      if (selectedDate) {
        setValue(format(selectedDate, 'YYYY-MM-DD') as T);
      } else {
        setValue(null as T);
      }
    }
  }

  const { inputProps, ...restDateInputProps } = dateInputProps || { inputProps: undefined };
  const { leftIcon: icon, ...restInputProps } = inputProps || { leftIcon: null };
  const leftIcon = icon && <Icon16 icon={icon} />;

  /**
   * This shouldn't need the portal: false, but navigating to the page via Turbolinks will
   * cause the datepicker to not show up unless we set it. I would have thought the portalContainer
   * fix in Thumbtack would have addressed the issue, but apparently not
   */
  return (
    <FormGroup {...formGroupProps} labelFor={name}>
      <DateInput
        value={value}
        onChange={handleChange}
        maxDate={maxDate}
        minDate={minDate}
        disabled={disabled}
        inputProps={{ ...field, intent: hasError ? Intent.DANGER : Intent.NONE, ...restInputProps, leftIcon }}
        popoverProps={{ usePortal: false, ...popoverProps }}
        {...restDateInputProps}
      />
      {hasError && <FormGroupFeedback intent={Intent.DANGER}>{meta.error}</FormGroupFeedback>}
    </FormGroup>
  );
}
